/**
 * React Query surface for the AI email-draft review workflow (SA-99).
 * One query (get, null on 404) + one mutation per verb, each invalidating
 * the shared draft key. Mirrors hooks/useMailboxes.ts.
 *
 * SA-106: the write verbs invalidate on SETTLED, not just on success — a
 * failure is exactly when the panel is most likely to be showing something
 * the server no longer agrees with (an ambiguous 502 leaves the intent
 * `sending`; a 409 means a racing send already claimed the row), so the
 * refetch has to happen on the error path too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { emailDraftsService } from '@/services/emailDrafts'

/** The panel's query key prefix; the record lane invalidates it after a write that drafts, rewords
 *  or discards an offer email (SA-1040 PR 2). */
export const EMAIL_DRAFT_KEY = ['email-draft'] as const
const key = (conversationId: string) => [...EMAIL_DRAFT_KEY, conversationId]

export function useEmailDraft(conversationId: string | null, enabled: boolean) {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: key(conversationId ?? 'none'),
    queryFn: () => emailDraftsService.get(conversationId!),
    enabled: enabled && !!conversationId,
  })
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: key(conversationId ?? 'none') })
  const update = useMutation({
    mutationFn: (body: { subject?: string; body_text?: string; quote_document_sha256?: string }) =>
      emailDraftsService.update(conversationId!, body),
    onSettled: invalidate,
  })
  const regenerate = useMutation({
    mutationFn: () => emailDraftsService.regenerate(conversationId!),
    onSuccess: invalidate,
  })
  const send = useMutation({
    mutationFn: (
      body?: { draft_id?: string; subject?: string; body_text?: string; quote_document_sha256?: string },
    ) => emailDraftsService.send(conversationId!, body),
    onSettled: invalidate,
  })
  const discard = useMutation({
    mutationFn: () => emailDraftsService.discard(conversationId!),
    onSettled: invalidate,
  })
  return {
    draft: query.data ?? null,
    isLoading: query.isLoading,
    // A real fetch failure (non-404 — auth, 500, network) vs. the
    // legitimate "no pending draft" null. See emailDraftsService.get.
    isError: query.isError,
    error: query.error,
    update,
    regenerate,
    send,
    discard,
  }
}
